import { type GitTaskMark } from '@apm/contracts';
import { useQuery } from '@tanstack/react-query';
import { GitPullRequest } from 'lucide-react';
import { type ReactNode, createContext, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../lib/utils';
import { gitApi, gitKeys } from '../api';
import { normalizeGitAsOf } from '../git-format';
import { useProjectGitConnected } from '../use-project-git-connected';
import { GitCiDot } from './git-marks';

/** id task → dấu. `null` = không có bản đồ (chưa tải, không quyền, ngoài bảng). */
const GitTaskMarksContext = createContext<Readonly<Record<string, GitTaskMark>> | null>(null);

/**
 * Nạp dấu MR của **cả bảng Kanban** bằng một lời gọi (`GET …/git/task-marks`) —
 * APM-GIT-i, docs/plan-git-integration.md §8.2: "Thẻ Kanban: một biểu tượng nhỏ
 * trạng thái MR + chấm CI khi task có MR đang mở".
 *
 * Context thay vì prop: thẻ là `memo` và nằm sau hai tầng `memo` khác của board
 * (APM-BOARD-perf4). Một prop mới đi qua cả ba tầng là ba chỗ để quên; context thì
 * chỉ `GitTaskMarkBadge` — một mẩu nhỏ trong thẻ — vẽ lại khi bản đồ đổi, còn thân
 * thẻ không.
 *
 * Người không có `git:read` ⇒ **không lời gọi nào** (query `enabled` theo quyền) và
 * thẻ không có dấu. Dự án chưa nối kho ⇒ cũng **không gọi** `…/task-marks` (APM-GIT-q,
 * `useProjectGitConnected` — danh sách kho dùng chung cache với tab Git): thẻ không
 * có dấu, đúng như khi chưa nối. "Đã nối" xét theo hiện tại, không theo `asOf`.
 *
 * Nhận `asOf` (luật #7): MR **đang mở tại mốc ấy**. Board hiện chỉ vẽ "bây giờ" nên
 * không truyền. ⚠️ Nháp / CI / review của MR luôn là giá trị hiện tại (`GitTaskMarks`).
 */
export function GitTaskMarksProvider({
  projectId,
  asOf,
  children,
}: {
  readonly projectId: string;
  readonly asOf?: string | undefined;
  readonly children: ReactNode;
}): ReactNode {
  const connection = useProjectGitConnected(projectId);
  const at = normalizeGitAsOf(asOf);
  const marks = useQuery({
    queryKey: gitKeys.taskMarks(projectId, at),
    queryFn: () => gitApi.taskMarks(projectId, at),
    enabled: connection.connected,
    // Dấu phụ trên thẻ: lỗi thì thẻ không có dấu, không làm phiền ai bằng toast.
    retry: false,
    staleTime: 60_000,
  });
  return (
    // Cache cũ từ lúc còn nối kho không được vẽ tiếp sau khi gỡ kho cuối cùng.
    <GitTaskMarksContext.Provider value={connection.connected ? (marks.data?.marks ?? null) : null}>
      {children}
    </GitTaskMarksContext.Provider>
  );
}

/**
 * Dấu nhỏ trên một thẻ: biểu tượng MR (kèm số khi hơn một MR đang mở) + chấm CI
 * xấu nhất của các MR ấy. Task không có MR đang mở, hay thẻ nằm ngoài
 * `GitTaskMarksProvider` ⇒ không render gì.
 *
 * ⚠️ Không nhận asOf (luật #7, ngoại lệ 1): thuần trình bày — mốc là việc của
 * `GitTaskMarksProvider`.
 */
export function GitTaskMarkBadge({ taskId }: { readonly taskId: string }): ReactNode {
  const { t } = useTranslation();
  const marks = useContext(GitTaskMarksContext);
  const mark = marks?.[taskId];
  if (!mark) return null;

  // Chấm CI tự mang chữ cho trình đọc màn hình — `spoken` không lặp lại nó.
  const spoken = [
    t('gitTab.taskMark.open', { n: mark.openChangeRequests }),
    mark.allDraft ? t('gitTab.common.state.draft') : null,
    t(`gitTab.common.review.${mark.reviewState}`),
  ].filter((part): part is string => part !== null);
  const parts =
    mark.worstCi === 'none' ? spoken : [...spoken, t(`gitTab.common.ci.${mark.worstCi}`)];

  return (
    <span
      data-testid="git-task-mark"
      title={parts.join(' · ')}
      className={cn(
        'inline-flex h-5 items-center gap-0.5 rounded-full border border-border px-1.5 text-[11px]',
        mark.allDraft ? 'text-muted-foreground' : 'text-primary',
      )}
    >
      <GitPullRequest aria-hidden className="size-3" />
      {mark.openChangeRequests > 1 ? (
        <span className="tabular-nums">{mark.openChangeRequests}</span>
      ) : null}
      <span className="sr-only">{spoken.join(' · ')}</span>
      <GitCiDot status={mark.worstCi} />
    </span>
  );
}
